import { CheckCircle, Circle, Loader2 } from 'lucide-react';

const RESEARCH_STEPS = [
  { key: 'parsing', label: 'Parsing input' },
  { key: 'questions', label: 'Formulating research questions' },
  { key: 'statutes', label: 'Researching statutes' },
  { key: 'cases', label: 'Researching case law' },
  { key: 'synthesis', label: 'Synthesizing legal analysis' },
  { key: 'pdf', label: 'Generating research document' },
];

function getStepIndex(progressPercent: number): number {
  if (progressPercent < 10) return 0;
  if (progressPercent < 25) return 1;
  if (progressPercent < 45) return 2;
  if (progressPercent < 70) return 3;
  if (progressPercent < 90) return 4;
  return 5;
}

interface ResearchProgressProps {
  progress: number;
  currentStep?: string;
  status: string;
}

export function ResearchProgress({ progress, currentStep, status }: ResearchProgressProps) {
  const activeStepIndex = getStepIndex(progress);

  return (
    <div className="bg-white rounded-lg border border-legal-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-semibold text-legal-900">Research in Progress</h3>
        <span className="text-2xl font-bold text-accent">{progress}%</span>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-legal-100 rounded-full h-3 mb-6">
        <div
          className="bg-accent h-3 rounded-full transition-all duration-700 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Current step text */}
      {currentStep && (
        <div className="flex items-center space-x-2 mb-6 p-3 bg-blue-50 rounded-lg">
          <Loader2 className="h-4 w-4 text-blue-500 animate-spin flex-shrink-0" />
          <p className="text-sm text-blue-700">{currentStep}</p>
        </div>
      )}

      {/* Steps */}
      <div className="space-y-3">
        {RESEARCH_STEPS.map((step, index) => {
          const isDone = index < activeStepIndex;
          const isActive = index === activeStepIndex && status === 'processing';
          return (
            <div key={step.key} className="flex items-center space-x-3">
              {isDone ? (
                <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
              ) : isActive ? (
                <Loader2 className="h-5 w-5 text-accent animate-spin flex-shrink-0" />
              ) : (
                <Circle className="h-5 w-5 text-legal-300 flex-shrink-0" />
              )}
              <span
                className={`text-sm ${
                  isDone ? 'text-legal-600 line-through' : isActive ? 'text-legal-900 font-medium' : 'text-legal-400'
                }`}
              >
                {step.label}
              </span>
            </div>
          );
        })}
      </div>

      <p className="mt-6 text-xs text-legal-400 text-center">
        Typical research takes 3–8 minutes. You'll be notified when it's ready.
      </p>
    </div>
  );
}
